import { useEffect, useMemo, useRef, useState } from "react";
import { Snackbar } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types.ts";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import type {
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Company, FromToLocation } from "@services/vessel-call/types";
import type { Country, Port, VesselType } from "@services/vessel/types";
import type { GetVesselCallsResponse } from "@services/vesselCall/types";
import { getSearchVesselCalls } from "@services/vesselCallApi";
import { useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import {
  buildFilterPayload,
  departureStatuses,
  findCompanyName,
  formatDateTime,
  formatRestriction,
  getDepartureStatusInfo,
  getStatusInfo,
  PORT_CALLS_FIELDS,
  sortAlphabetically,
  statuses,
} from "./utils";

export const LANG_BASE = "vessel-call.portCalls";
export const LANG_BASE_HEADER = `${LANG_BASE}.headerLabels`;
const TEST_ID_BASE = "port-calls";

const LOCAL_STORAGE_PAGE_KEY = "port-calls-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "port-calls-page-size";
const LOCAL_STORAGE_FILTERS_KEY = "port-calls-filters";
const SEARCH_INQUIRY_ID = "vessel-call.port-calls.search";
const SEARCH_INQUIRY_CONTEXT = {
  module: "vessel_call",
  screen: "Port Calls",
  action: "search_vessel_calls",
  entityType: "vessel_call",
};

export interface PaginationParams {
  pageSize: number;
  page: number;
}

export const PortCalls = () => {
  const { t } = useTranslation();
  const { userAccessPermissions } = useAuthStore();

  const [filters, setFilters] = useState<FilterItem[]>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? parseInt(saved, 10) : 25;
  });
  const vesselService = "bff-vessel";
  const vesselCallService = "bff-vessel-call";

  const isMarineDepartment = checkPermission(userAccessPermissions, "hasChangeVesselStatusAccess");

  const { options: vesselTypes = [] } = useFormattedReference<VesselType>(
    REFERENCE_DATA_TYPES.VESSEL_TYPES,
    vesselService,
  );

  const { options: ports = [] } = useFormattedReference<Port>(
    REFERENCE_DATA_TYPES.PORTS,
    vesselService,
  );

  const { options: countries = [] } = useFormattedReference<Country>(
    REFERENCE_DATA_TYPES.COUNTRIES,
    vesselService,
  );

  const { options: fromToLocations = [] } = useFormattedReference<FromToLocation>(
    REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS,
    vesselCallService,
  );

  const { options: companies = [] } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isShippingAgency=true`,
    vesselCallService,
  );

  const headers: Header[] = useMemo(() => {
    let baseHeaders = Object.values(PORT_CALLS_FIELDS).map((field) => ({
      field,
      columnName: t(`${LANG_BASE_HEADER}.${field}`),
    }));

    if (!isMarineDepartment) {
      baseHeaders = baseHeaders.filter(
        ({ field }) =>
          field !== "verifiedBy" && field !== "approvedBy" && field !== "departureStatus",
      );
    }
    return isMarineDepartment
      ? [
          ...baseHeaders,
          { field: "restrictions", columnName: t(`${LANG_BASE_HEADER}.restrictions`) },
        ]
      : baseHeaders;
  }, [t, isMarineDepartment]);

  const {
    data: vesselCallsData,
    isLoading: isVesselCallsIsLoading,
    isFetching: isVesselCallsFetching,
    isError: isVesselCallsError,
    isSuccess: isVesselCallsSuccess,
    dataUpdatedAt: vesselCallsDataUpdatedAt,
    error,
  } = useQuery<GetVesselCallsResponse>({
    queryKey: ["vesselCall", page, pageSize, filters],
    queryFn: () =>
      getSearchVesselCalls(vesselCallService, buildFilterPayload(page, pageSize, filters)),
  });
  const { vesselCalls = [], totalRecordCount = 0 } = vesselCallsData ?? {};

  useTrackPendingInquiry(SEARCH_INQUIRY_ID, {
    isFetching: isVesselCallsFetching,
    isLoading: isVesselCallsIsLoading,
    isError: isVesselCallsError,
    isSuccess: isVesselCallsSuccess,
    error,
    dataUpdatedAt: vesselCallsDataUpdatedAt,
  });

  // AppInsights: Track initial screen load time
  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);

  useEffect(() => {
    if (isVesselCallsIsLoading || error || hasTrackedInitialLoad.current) {
      return;
    }

    hasTrackedInitialLoad.current = true;

    completeScreenNavigation();
    trackScreenLoad("Vessel Call Management", "Vessel Call", startedAt.current);
  }, [isVesselCallsIsLoading, error]);

  const vesselTypesOptions = useMemo(() => {
    return (
      vesselTypes
        .filter(
          (
            vesselType,
          ): vesselType is VesselType & { vesselTypeCode: string; vesselTypeDesc: string } =>
            !!(vesselType?.vesselTypeCode && vesselType?.vesselTypeDesc),
        )
        .map(({ vesselTypeDesc, vesselTypeCode }) => ({
          label: vesselTypeDesc.toUpperCase(),
          value: vesselTypeCode,
          key: vesselTypeCode,
        })) ?? []
    );
  }, [vesselTypes]);

  const portOptions = useMemo(
    () =>
      ports
        .filter(
          (port): port is Port & { portCode: string; portName: string } =>
            !!(port?.portCode && port?.portName),
        )
        .map(({ portName, portCode }) => ({
          label: `${portName} (${portCode})`,
          value: portCode,
          key: portCode,
        })),
    [ports],
  );

  const locationOptions = useMemo(
    () =>
      fromToLocations
        .filter(
          (
            location,
          ): location is FromToLocation & {
            fromToLocationCode: string;
            locationName: string;
          } => !!(location?.fromToLocationCode && location?.locationName),
        )
        .map(({ fromToLocationCode, locationName }) => ({
          label: locationName,
          value: fromToLocationCode,
          key: fromToLocationCode,
        })),
    [fromToLocations],
  );

  const flagsOptions = useMemo(
    () =>
      countries
        .filter(
          (country): country is Country & { countryCode: string; countryName: string } =>
            !!(country?.countryCode && country?.countryName),
        )
        .map(({ countryCode, countryName }) => ({
          label: countryName,
          value: countryName.toUpperCase(),
          key: countryCode,
        })),
    [countries],
  );

  const filterFields: Record<string, FilterFieldDefinition> = useMemo(
    () => ({
      imo: {
        label: t(`${LANG_BASE_HEADER}.imo`),
        type: "integer",
        minLength: 3,
      },
      vcn: {
        label: t(`${LANG_BASE_HEADER}.vcn`),
        type: "integer",
        minLength: 3,
      },
      type: {
        label: t(`${LANG_BASE_HEADER}.type`),
        type: "autocomplete",
        options: sortAlphabetically([...vesselTypesOptions]),
      },
      name: {
        label: t(`${LANG_BASE_HEADER}.name`),
        type: "text",
        minLength: 3,
      },
      flag: {
        label: t(`${LANG_BASE_HEADER}.flag`),
        type: "autocomplete",
        options: sortAlphabetically([...flagsOptions]),
      },
      agencyName: {
        label: t(`${LANG_BASE_HEADER}.agencyName`),
        type: "text",
        minLength: 3,
      },
      lastPort: {
        label: t(`${LANG_BASE_HEADER}.lastPort`),
        type: "autocomplete",
        options: sortAlphabetically([...portOptions]),
      },
      nextPort: {
        label: t(`${LANG_BASE_HEADER}.nextPort`),
        type: "autocomplete",
        options: sortAlphabetically([...portOptions]),
      },
      visitPurpose: {
        label: t(`${LANG_BASE_HEADER}.visitPurpose`),
        type: "text",
        minLength: 3,
      },
      arrivalLocation: {
        label: t(`${LANG_BASE_HEADER}.arrivalLocation`),
        type: "autocomplete",
        options: sortAlphabetically([...locationOptions]),
      },
      currentLocation: {
        label: t(`${LANG_BASE_HEADER}.currentLocation`),
        type: "autocomplete",
        options: sortAlphabetically([...locationOptions]),
      },
      ...(isMarineDepartment && {
        departureStatus: {
          label: t(`${LANG_BASE_HEADER}.departureStatus`),
          options: sortAlphabetically(
            [...departureStatuses].map(({ value, label }) => ({
              value,
              label: t(`${label}`),
            })),
          ),
          type: "autocomplete",
        },
      }),
      status: {
        label: t(`${LANG_BASE_HEADER}.status`),
        options: sortAlphabetically(
          [...statuses].map(({ value, label }) => ({
            value,
            label: t(`vessel-call.status.${label}`),
          })),
        ),
        type: "autocomplete",
      },
      ...(isMarineDepartment && {
        verifiedBy: { label: t(`${LANG_BASE_HEADER}.verifiedBy`), type: "text", minLength: 3 },
        approvedBy: { label: t(`${LANG_BASE_HEADER}.approvedBy`), type: "text", minLength: 3 },
      }),
    }),
    [t, vesselTypesOptions, flagsOptions, portOptions, locationOptions, isMarineDepartment],
  );

  const onApplyFilters = (filters: FilterItem[]) => {
    startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    setFilters(filters);
    localStorage.setItem(LOCAL_STORAGE_FILTERS_KEY, JSON.stringify(filters));
    setPage(1);
    localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, "1");
  };

  const onClearFilters = () => {
    if (filters.length) {
      startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    }
    setFilters([]);
    localStorage.removeItem(LOCAL_STORAGE_FILTERS_KEY);
  };
  const na = useMemo(() => t("common.notApplicable"), [t]);
  const formattedFormData = vesselCalls.map(
    ({
      vesselCallId,
      vesselId,
      imo,
      vcn,
      submittedAt,
      typeDesc,
      name,
      flag,
      agencyName,
      lastPort,
      nextPort,
      visitPurpose,
      eta,
      arrivalLocation,
      ata,
      atd,
      currentLocation,
      verifiedBy,
      approvedBy,
      departureStatus,
      status,
      restriction,
    }) => {
      return {
        id: vesselCallId,
        vesselId: vesselId,
        imo: imo,
        vcn: vcn,
        submittedAt: formatDateTime(submittedAt),
        type: typeDesc?.toUpperCase(),
        name: name,
        flag: flag,
        agencyName: findCompanyName(companies, agencyName),
        lastPort: lastPort,
        nextPort: nextPort,
        visitPurpose: visitPurpose,
        eta: formatDateTime(eta),
        arrivalLocation: arrivalLocation,
        ata: formatDateTime(ata),
        atd: formatDateTime(atd),
        currentLocation: currentLocation,
        verifiedBy: verifiedBy,
        approvedBy: approvedBy,
        ...(isMarineDepartment && {
          departureStatusBadgeType: getDepartureStatusInfo(departureStatus).badgeType,
          departureStatus: t(getDepartureStatusInfo(departureStatus).label),
        }),
        status: t(getStatusInfo(status)),
        ...(isMarineDepartment && {
          restrictions: formatRestriction(restriction, t, na),
        }),
      };
    },
  );

  if (isVesselCallsIsLoading) {
    return <LoadingOverlay loading={isVesselCallsIsLoading} />;
  }

  return (
    <div data-testid={TEST_ID_BASE}>
      {error && <Snackbar open autoHideDuration={6000} message={error.message} />}
      {!isVesselCallsIsLoading && (
        <CustomDataTable
          testId={TEST_ID_BASE}
          title={t(`${LANG_BASE}.title`)}
          actionName={t(`${LANG_BASE}.addVesselCall`)}
          headers={headers}
          rows={formattedFormData}
          page={page}
          pageSize={pageSize}
          totalCount={totalRecordCount}
          canPreview={true}
          onPageChange={(newPage, newSize) => {
            if (newPage !== page || newSize !== pageSize) {
              startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
            }
            setPage(newPage);
            setPageSize(newSize);
            localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
            localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
          }}
          navigationLinkBase="/vessel-call/vessel-call-status"
          navigationParam="vesselCallId"
          pageSizeOptions={[5, 10, 15, 20, 25]}
          filters={filters}
          onApplyFilters={onApplyFilters}
          onClearFilters={onClearFilters}
          availableFields={filterFields}
          shouldFreezeExtraColumn={isMarineDepartment}
        />
      )}
    </div>
  );
};

export default PortCalls;
